import { useEffect, useState } from 'react';
import { cn } from '../../../lib/utils';
import {
  cardClass,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  graphPurpleColor,
  graphOrangeColor,
} from '../../../styling/stylingHelper';
import {
  getAdminRevenueStats,
  AdminRevenueStats,
} from '../../../services/admin/adminService';
import { GraphCard } from '../../ui/GraphCard';
import { AdminLayout } from './AdminLayout';

const AdminRevenuePage = () => {
  const [revenueStats, setRevenueStats] = useState<AdminRevenueStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const revenue = await getAdminRevenueStats();
        setRevenueStats(revenue);
      } catch (error) {
        console.error('Failed to load revenue stats:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className={cn(secondaryText)}>Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  const weeklyChartData = revenueStats?.revenueByWeek.map(w => ({
    label: w.week.slice(5, 10),
    value: w.totalRevenue,
  })) ?? [];

  const sourcesByRevenue = [...(revenueStats?.revenueBySourceType ?? [])].sort((a, b) => b.totalRevenue - a.totalRevenue);
  const sourcesByEntries = [...(revenueStats?.revenueBySourceType ?? [])].sort((a, b) => b.entryCount - a.entryCount);
  const maxRevenue = Math.max(...(revenueStats?.revenueBySourceType.map(s => s.totalRevenue) ?? [1]), 1);
  const maxEntries = Math.max(...(revenueStats?.revenueBySourceType.map(s => s.entryCount) ?? [1]), 1);

  const avgRevenuePerEntry = revenueStats?.totalRevenueEntries
    ? revenueStats.totalRevenueTracked / revenueStats.totalRevenueEntries
    : 0;

  return (
    <AdminLayout>
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className={cn(cardClass, 'p-4')}>
          <div className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-1')}>Total Revenue</div>
          <div className={cn(primaryText, 'text-2xl', semiBoldText)}>
            {formatCurrency(revenueStats?.totalRevenueTracked ?? 0)}
          </div>
        </div>
        <div className={cn(cardClass, 'p-4')}>
          <div className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-1')}>Revenue Entries</div>
          <div className={cn(primaryText, 'text-2xl', semiBoldText)}>
            {(revenueStats?.totalRevenueEntries ?? 0).toLocaleString()}
          </div>
        </div>
        <div className={cn(cardClass, 'p-4')}>
          <div className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-1')}>Source Types</div>
          <div className={cn(primaryText, 'text-2xl', semiBoldText)}>
            {revenueStats?.revenueBySourceType.length ?? 0}
          </div>
        </div>
        <div className={cn(cardClass, 'p-4')}>
          <div className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-1')}>Avg per Entry</div>
          <div className={cn(primaryText, 'text-2xl', semiBoldText)}>
            {formatCurrency(avgRevenuePerEntry)}
          </div>
        </div>
      </div>

      {/* Revenue Over Time Chart */}
      <div className="mb-6">
        <GraphCard
          title="Revenue Per Week"
          xLabels={weeklyChartData.map(d => d.label)}
          series={[
            {
              label: 'Revenue',
              values: weeklyChartData.map(d => d.value),
              color: graphOrangeColor,
            },
          ]}
          height={300}
        />
      </div>

      {/* Two side-by-side bar charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by Source Type */}
        <div className={cn(cardClass, 'p-5')}>
          <h3 className={cn(primaryText, semiBoldText, 'mb-4')}>Revenue by Source Type</h3>
          {sourcesByRevenue.length === 0 ? (
            <div className={cn(secondaryText, smallTextSize)}>No revenue data yet</div>
          ) : (
            <div className="space-y-4">
              {sourcesByRevenue.map((source, index) => {
                const percentage = (source.totalRevenue / maxRevenue) * 100;
                return (
                  <div key={source.sourceType}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className={cn('text-xs', secondaryText, 'w-4')}>{index + 1}</span>
                        <span className={cn(primaryText, smallTextSize, semiBoldText)}>{source.sourceType}</span>
                      </div>
                      <span className={cn(primaryText, smallTextSize)}>{formatCurrency(source.totalRevenue)}</span>
                    </div>
                    <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2.5">
                      <div
                        className="h-2.5 rounded-full transition-all duration-300"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: graphOrangeColor,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Entries by Source Type */}
        <div className={cn(cardClass, 'p-5')}>
          <h3 className={cn(primaryText, semiBoldText, 'mb-4')}>Entries by Source Type</h3>
          {sourcesByEntries.length === 0 ? (
            <div className={cn(secondaryText, smallTextSize)}>No revenue data yet</div>
          ) : (
            <div className="space-y-4">
              {sourcesByEntries.map((source, index) => {
                const percentage = (source.entryCount / maxEntries) * 100;
                return (
                  <div key={source.sourceType}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className={cn('text-xs', secondaryText, 'w-4')}>{index + 1}</span>
                        <span className={cn(primaryText, smallTextSize, semiBoldText)}>{source.sourceType}</span>
                      </div>
                      <span className={cn(primaryText, smallTextSize)}>{source.entryCount.toLocaleString()} entries</span>
                    </div>
                    <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2.5">
                      <div
                        className="h-2.5 rounded-full transition-all duration-300"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: graphPurpleColor,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminRevenuePage;
